import { useMemo } from 'react';

import type { PortfolioDiversification as NormalisedPortfolioDiversification } from '../../types';

import { collapseChildren } from '../../utils';
import type { PortfolioDiversification } from '../usePortfolioDiversification';

type Diversification = NonNullable<PortfolioDiversification['diversification']>;

type Child = keyof Pick<
  Diversification,
  'sectors' | 'primaryIndustries' | 'holdings'
>;

const children: Child[] = ['sectors', 'primaryIndustries', 'holdings'];

const isValidChild = (child?: string): child is Child => {
  if (typeof child === 'undefined') return false;
  return children.includes(child as Child);
};

export const normaliseData = (
  tree: Diversification
): NormalisedPortfolioDiversification => {
  const { name, weight, symbol } = tree;
  const key = children.find((n) => typeof tree[n] !== 'undefined');
  if (isValidChild(key)) {
    const children = tree[key] ?? [];
    return {
      name: name || symbol || '',
      weight,
      childCount: children?.length ?? 0,
      children: children.map((n) => normaliseData(n)),
    };
  }
  return {
    name: name || symbol || '',
    weight,
    childCount: 0,
    children: [],
  };
};

export const collapse = (
  data: NormalisedPortfolioDiversification
): NormalisedPortfolioDiversification => ({
  ...data,
  children: data.children?.reduce<NormalisedPortfolioDiversification[]>(
    (a, b) => [
      ...a,
      {
        ...b,
        children: (b.children || []).map((n) => collapseChildren(n)),
      },
    ],
    []
  ),
});

type Args = {
  diversification: Diversification;
  collapseHoldings?: boolean;
};

export const useNormalisedDiversification = ({
  diversification,
  collapseHoldings = false,
}: Args) => {
  return useMemo(() => {
    const data = normaliseData({
      ...diversification,
      name: 'Portfolio',
    });

    if (collapseHoldings) {
      return collapse(data);
    }

    return data;
  }, [diversification, collapseHoldings]);
};

export type NormalisedDiversification = ReturnType<
  typeof useNormalisedDiversification
>;
